/**
 * Formatos de papel para geração de PDF — A4 (o de sempre) e as duas bobinas
 * térmicas de balcão.
 *
 * ─── POR QUE TÉRMICA NÃO É UM `PdfTemplateName` ──────────────────────────────
 *
 * Seria tentador registrar "termico" como um 10º template e pegar de graça o
 * seletor por documento (`quotes.pdf_template`). Seria errado por três motivos,
 * e o terceiro é o que morde:
 *
 *  1. `generateQuotePDF` cria o `jsPDF` ANTES de saber o template — formato de
 *     página é uma decisão anterior à de template, não uma variante dele.
 *  2. Em 72 mm não existe diferenciação visual possível (sem cor, sem logo, sem
 *     foto), então os 9 templates colapsariam num só de qualquer jeito.
 *  3. O template é PERSISTIDO no documento. Um orçamento salvo como "térmico"
 *     sairia em bobina quando o CLIENTE baixasse pela página pública `/o/:id`,
 *     no navegador dele, para imprimir em casa. Térmica é escolha do momento de
 *     IMPRIMIR, nunca propriedade do documento.
 *
 * Por isso o formato é ortogonal ao template e vive só na chamada.
 */

export type PaperFormat = 'a4' | 'thermal80' | 'thermal58';
export type ThermalFormat = Exclude<PaperFormat, 'a4'>;

export interface ThermalSpec {
  id: ThermalFormat;
  /** Rótulo curto para o menu ("80 mm"). */
  label: string;
  /** Uma linha explicando quando usar. */
  description: string;
  /**
   * Largura FÍSICA da bobina em mm — é o formato da página do PDF.
   *
   * Usamos a largura do papel (e não a área imprimível) de propósito: o serviço
   * de impressão do Android e os apps das impressoras encaixam a página na
   * bobina, então uma página de 80 mm sai 1:1 numa bobina de 80 mm. Passar a
   * área imprimível (72 mm) faria a impressora ampliar para preencher os 80 mm,
   * e a margem que reservamos aqui viraria conteúdo cortado nas bordas.
   */
  paperWidth: number;
  /**
   * Margem lateral em mm. `paperWidth - 2 * margin` é a área útil, calibrada
   * para a faixa imprimível típica de cada bobina (72 mm em 80, 48 mm em 58).
   */
  margin: number;
}

export const THERMAL_SPECS: Record<ThermalFormat, ThermalSpec> = {
  thermal80: {
    id: 'thermal80',
    label: '80 mm',
    description: 'Bobina padrão de balcão',
    paperWidth: 80,
    margin: 4,
  },
  thermal58: {
    id: 'thermal58',
    label: '58 mm',
    description: 'Bobina pequena / impressora portátil',
    paperWidth: 58,
    margin: 5,
  },
};

export const THERMAL_FORMATS: ThermalFormat[] = ['thermal80', 'thermal58'];

export const DEFAULT_THERMAL_FORMAT: ThermalFormat = 'thermal80';

export const isThermalFormat = (paper: PaperFormat): paper is ThermalFormat =>
  paper !== 'a4';

export const thermalSpec = (format: ThermalFormat): ThermalSpec =>
  THERMAL_SPECS[format] ?? THERMAL_SPECS[DEFAULT_THERMAL_FORMAT];

// ---------------------------------------------------------------------------
// Preferência de largura — POR DISPOSITIVO, não por empresa.
// ---------------------------------------------------------------------------
/**
 * A bobina é propriedade da IMPRESSORA, e a impressora está pareada a um
 * aparelho específico (o do balcão). Dois aparelhos da mesma empresa podem ter
 * impressoras diferentes — um tablet no caixa com 80 mm e um celular na
 * entrega com uma portátil de 58 mm. Guardar isso em `companies` obrigaria os
 * dois a concordarem, além de exigir migração e o checklist do
 * `CompanyContext` (que monta insert/update por lista explícita de colunas e
 * descarta coluna nova em silêncio).
 *
 * Fica no `localStorage` do aparelho: sem migração, funciona offline e é a
 * granularidade correta.
 */
const STORAGE_KEY = 'orcamigo_thermal_paper';

export const readThermalPreference = (): ThermalFormat => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && raw in THERMAL_SPECS) return raw as ThermalFormat;
  } catch {
    // localStorage indisponível (modo privado / WebView restrita) — o padrão
    // serve, e o usuário só perde a memória da escolha.
  }
  return DEFAULT_THERMAL_FORMAT;
};

export const writeThermalPreference = (format: ThermalFormat): void => {
  try {
    localStorage.setItem(STORAGE_KEY, format);
  } catch {
    /* idem: não poder lembrar não pode impedir de imprimir */
  }
};

// ---------------------------------------------------------------------------
// Quais documentos podem sair na bobina
// ---------------------------------------------------------------------------
/**
 * O CONTRATO fica de fora, e não por limitação técnica: ele precisa de duas
 * assinaturas, texto multi-parágrafo e rodapé de auditoria — e papel térmico
 * desbota em meses. Contrato em bobina é um documento probatório que apaga
 * sozinho. Quem pedir térmica para contrato recebe erro explícito
 * (`assertThermalSupported`) em vez de um A4 silencioso, que esconderia o
 * motivo.
 */
export const THERMAL_DOCUMENT_TYPES = [
  'orcamento',
  'pedido',
  'recibo',
  'ordem_servico',
] as const;

export type ThermalDocumentType = (typeof THERMAL_DOCUMENT_TYPES)[number];

export const supportsThermal = (documentType: string): documentType is ThermalDocumentType =>
  (THERMAL_DOCUMENT_TYPES as readonly string[]).includes(documentType);

export function assertThermalSupported(documentType: string): void {
  if (!supportsThermal(documentType)) {
    throw new Error(
      `Impressão térmica não disponível para "${documentType}". ` +
        'Contrato exige assinaturas e trilha de auditoria, e papel térmico desbota — use o PDF A4.',
    );
  }
}
